// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.
/**
 * main.ts — bootstrap and screen wiring. Owns no game logic.
 *
 * The shape: menu -> (solo | room entry -> lobby) -> countdown -> round ->
 * results -> (rematch inside the same room | back to lobby | menu).
 *
 * The rule that governs this file: ONE ROOM PER SESSION. The Net is created once
 * when you enter a room and lives until you leave for the menu. "Play again"
 * never touches it — rematch.ts versions rounds inside the living room. Two
 * games in this factory shipped broken by leaving and re-joining to "reset".
 */

import './styles/mobile.css';
import './styles/main.css';

import { Game, radiusOf, START_MASS, type Seat } from './game';
import { MODE_LIST, MAX_PLAYERS, modeOf, DEFAULT_MODE, type Mode } from './modes';
import { createSession, type Session, type SessionSeat } from './net-game';
import { createRenderer } from './render';
import { createFx, colorOf } from './fx';
import { createSfx } from './sound';
import { startCountdown, type Countdown } from './countdown';
import { summarize, tallyRound, emptyTally, renderSummary, shareText, type MatchTally } from './results';
import { createLoop } from '@ben-gy/game-engine/loop';
import { createInput } from '@ben-gy/game-engine/input';
import { createJoystick } from '@ben-gy/game-engine/joystick';
import { createStore } from '@ben-gy/game-engine/storage';
import { createNet, roomAppId, setTurnConfig, type Net } from '@ben-gy/game-engine/net';
import { getTurnConfig } from '@ben-gy/game-engine/turn';
import { createRounds, type Rounds } from '@ben-gy/game-engine/rematch';
import { resolveName, withName } from '@ben-gy/game-engine/identity';
import { hardenViewport } from '@ben-gy/game-engine/mobile';
import {
  createLobby,
  createRoomEntry,
  mintCode,
  normalizeRoomCode,
  clearRoomInUrl,
  setRoomInUrl,
} from '@ben-gy/game-engine/lobby';
import { newSeed } from '@ben-gy/game-engine/rng';
import { createNoticeboard, type Noticeboard, type PublicRoom } from '@ben-gy/game-engine/noticeboard';

hardenViewport();

/**
 * The repo slug. Every mesh this page opens — the game room AND the public
 * noticeboard — is namespaced off it via `roomAppId()`, which folds in the
 * engine's protocol revision so a player on a stale cached build lands in a
 * different signaling namespace instead of half-connecting and desyncing.
 */
const SLUG = 'morsel';
const APP_ID = roomAppId(SLUG);

// The RAW slug, deliberately — local settings (name, mute) must survive a
// protocol bump, so the storage namespace never carries the revision.
const store = createStore(SLUG);
const app = document.querySelector<HTMLDivElement>('#app')!;

const BLOB_NAMES = [
  'Nucleus', 'Vesicle', 'Plasm', 'Cilia', 'Flagella', 'Spore', 'Amoeba', 'Zoid',
];

const sfx = createSfx(store.get('muted', false));
let myName = resolveName(store, () => 'Blob');

// ── screens ─────────────────────────────────────────────────────────────────

let net: Net | null = null;
let rounds: Rounds | null = null;
let session: Session | null = null;
let game: Game | null = null;
let countdown: Countdown | null = null;
let tally: MatchTally = emptyTally();
let roundNo = 0;
/** Our seat in the current round, taken from the host's frozen roster. */
let mySeat = 0;
let roomCode = '';
let mode: Mode = modeOf(store.get('mode', DEFAULT_MODE.id));
/** Host-gossiped mode for the current room; null until heard. */
let liveMode: Mode = mode;
let deepLinkUsed = false;

// ── public drop-in ──────────────────────────────────────────────────────────
/** True when the current room is a public one (open to anyone, drop-in). */
let roomPublic = false;
/** The public-room list, held only while browsing/hosting a public dish. */
let board: Noticeboard | null = null;
let publicRooms: PublicRoom[] = [];
let matchTimer: ReturnType<typeof setTimeout> | null = null;
let announceTimer: ReturnType<typeof setInterval> | null = null;

const el = (html: string): HTMLElement => {
  const d = document.createElement('div');
  d.innerHTML = html.trim();
  return d.firstElementChild as HTMLElement;
};

const FOOTER = `<footer class="site-footer">
  Built by <a href="https://benrichardson.dev/" target="_blank" rel="noopener">benrichardson.dev</a>
  · <a class="hub-link" href="https://hub.benrichardson.dev" target="_blank" rel="noopener">more games, tools &amp; sites</a>
</footer>`;

function shell(inner: string): void {
  // Every screen shows the footer EXCEPT a live round (showGame re-adds `playing`).
  document.body.classList.remove('playing');
  app.innerHTML = `<div class="main-content">${inner}</div>${FOOTER}`;
  const hub = app.querySelector<HTMLAnchorElement>('.hub-link');
  if (hub) hub.href = withName('https://hub.benrichardson.dev', myName);
}

function esc(s: string): string {
  return s.replace(/[&<>"']/g, (c) =>
    c === '&' ? '&amp;' : c === '<' ? '&lt;' : c === '>' ? '&gt;' : c === '"' ? '&quot;' : '&#39;',
  );
}

// ── menu ────────────────────────────────────────────────────────────────────

function showMenu(): void {
  teardownRoom();
  closeBoard();
  clearRoomInUrl();

  shell(`
    <div class="menu">
      <h1 class="title">Morsel</h1>
      <p class="tagline">Eat the small ones. Outrun the big ones.<br/>Catching anyone costs you the mass you were winning with.</p>

      <div class="modes" role="radiogroup" aria-label="Mode">
        ${MODE_LIST.map(
          (m) => `<button class="mode${m.id === mode.id ? ' on' : ''}" role="radio"
            aria-checked="${m.id === mode.id}" data-mode="${m.id}">
            <b>${m.name}</b><span>${esc(m.blurb)}</span></button>`,
        ).join('')}
      </div>

      <div class="menu-actions">
        <button class="btn primary" id="play">Play solo</button>
        <button class="btn online" id="online">Play online</button>
        <button class="btn" id="friends">Play with friends</button>
      </div>

      <label class="namebox">Your name
        <input id="name" maxlength="12" value="${esc(myName)}" autocomplete="off" spellcheck="false" />
      </label>

      <div class="menu-links">
        <button class="btn ghost" id="how">How to play</button>
        <button class="btn ghost" id="about">About</button>
        <button class="btn ghost" id="mute">${sfx.muted() ? 'Sound off' : 'Sound on'}</button>
      </div>
      <p class="best">${bestLine()}</p>
    </div>`);

  for (const b of app.querySelectorAll<HTMLElement>('.mode')) {
    b.addEventListener('click', () => {
      mode = modeOf(b.dataset.mode);
      liveMode = mode;
      store.set('mode', mode.id);
      sfx.unlock();
      sfx.play('select');
      showMenu();
    });
  }

  app.querySelector('#play')!.addEventListener('click', () => {
    sfx.unlock();
    startSolo();
  });
  app.querySelector('#online')!.addEventListener('click', () => {
    sfx.unlock();
    showPublic();
  });
  app.querySelector('#friends')!.addEventListener('click', () => {
    sfx.unlock();
    showRoomEntry();
  });
  app.querySelector('#how')!.addEventListener('click', () => showHelp());
  app.querySelector('#about')!.addEventListener('click', showAbout);
  app.querySelector('#mute')!.addEventListener('click', () => {
    sfx.setMuted(!sfx.muted());
    store.set('muted', sfx.muted());
    sfx.unlock();
    sfx.play('select');
    showMenu();
  });

  const name = app.querySelector<HTMLInputElement>('#name')!;
  name.addEventListener('change', () => {
    myName = name.value.trim().slice(0, 12) || 'Blob';
    store.set('name', myName);
    name.value = myName;
  });

  if (!store.get('seen-help', false)) showHelp();
}

function bestLine(): string {
  const best = store.get<number>(`best:${mode.id}`, 0);
  return best > 0 ? `Your best ${mode.name}: ${Math.round(best)} mass` : '';
}

// ── help / about ────────────────────────────────────────────────────────────

function modal(title: string, body: string, onClose?: () => void): void {
  const m = el(`<div class="modal" role="dialog" aria-modal="true" aria-label="${esc(title)}">
    <div class="modal-card">
      <h2>${esc(title)}</h2>
      ${body}
      <button class="btn primary modal-x">Got it</button>
    </div>
  </div>`);
  document.body.appendChild(m);
  const close = (): void => {
    m.remove();
    onClose?.();
  };
  m.querySelector('.modal-x')!.addEventListener('click', close);
  m.addEventListener('click', (e) => {
    if (e.target === m) close();
  });
}

function showHelp(): void {
  store.set('seen-help', true);
  modal(
    'How to play',
    `<ul class="how">
      <li><b>Eat the pellets.</b> They are worth more and more as the round runs down, so the last stretch decides it.</li>
      <li><b>Swallow anything smaller.</b> Run from anything bigger. Size is the only thing that matters, and you can see it.</li>
      <li><b>Big blobs are slow.</b> So the only way to catch anyone is to <b>DASH</b> — and a dash costs 15% of your mass, sprayed out behind you as food for whoever you are chasing.</li>
      <li><b>Whoever is biggest at the whistle wins.</b> Getting eaten just costs you a moment — you are never out.</li>
    </ul>
    <p class="how-ctl"><b>Move:</b> aim with the mouse, or push the on-screen stick with your thumb — rest it anywhere.
    <b>Dash:</b> a quick tap, space, or the DASH button.</p>`,
  );
}

function showAbout(): void {
  modal(
    'About Morsel',
    `<p>A petri dish, eight blobs and one rule: everything smaller than you is lunch.</p>
     <p>Play solo against bots, or share a room code with friends — up to ${MAX_PLAYERS} of you in the same dish.</p>
     <p class="fine">Multiplayer is <b>peer-to-peer</b>: your browsers talk directly to each other over WebRTC and there is no game server.
     A free public signaling relay only brokers the initial handshake — after that, nothing about your game touches anyone's server, and nothing is stored.</p>
     <p class="fine">No cookies, no fingerprinting, no third-party fonts. Anonymous, cookie-less page-view counts via Cloudflare Web Analytics.</p>
     <p class="fine">Built by <a href="https://benrichardson.dev/" target="_blank" rel="noopener">benrichardson.dev</a>.</p>`,
  );
}

// ── room entry + lobby ──────────────────────────────────────────────────────

function showRoomEntry(): void {
  teardownRoom();
  closeBoard();
  shell('<div class="screen" id="entry"></div>');
  createRoomEntry({
    container: app.querySelector<HTMLElement>('#entry')!,
    onSubmit: (code, created) => enterRoom(normalizeRoomCode(code), created),
    onCancel: showMenu,
    subtitle: `Start a room and share the code, or type a friend's. Up to ${MAX_PLAYERS} blobs.`,
  });
}

// ── public drop-in ──────────────────────────────────────────────────────────
// "Play online" drops you straight into a shared dish with whoever else is on
// right now — no code to swap, bots filling any empty seats so it is fun even
// when you are first in. It reuses the exact one-room-per-session flow below:
// matchmaking picks (or mints) a public room, then enterRoom joins it ONCE. The
// noticeboard (a separate room) advertises open dishes and carries the live
// counts. Public play is opt-in and the IP cost is disclosed once, up front.

const PUBLIC_MAX = MAX_PLAYERS;

function openBoard(): void {
  if (board) return;
  board = createNoticeboard({
    appId: APP_ID,
    onRooms: (r) => {
      publicRooms = r;
      paintPublicCounts();
    },
  });
}

/** Leave the public list and stop advertising. */
function closeBoard(): void {
  if (matchTimer) {
    clearTimeout(matchTimer);
    matchTimer = null;
  }
  if (announceTimer) {
    clearInterval(announceTimer);
    announceTimer = null;
  }
  roomPublic = false;
  const b = board;
  board = null;
  publicRooms = [];
  void b?.destroy();
}

/** Total humans across every advertised public dish — honest social proof. */
function publicOnline(): number {
  return publicRooms.reduce((n, r) => n + r.players, 0);
}

function paintPublicCounts(): void {
  const online = publicOnline();
  const here = net ? net.count() + 1 : 0;
  for (const elc of document.querySelectorAll<HTMLElement>('.pub-count')) {
    elc.textContent =
      roomPublic && net
        ? `${here} in this dish · ${Math.max(online, here)} playing online`
        : online > 0
          ? `${online} playing online right now — join in`
          : 'Be the first in — bots keep the dish busy until others arrive';
  }
}

function showPublic(): void {
  teardownRoom();
  closeBoard();
  if (!store.get('seen-public', false)) return showPublicIntro();
  startMatchmaking();
}

/** The one-time WebRTC/IP disclosure, shown before a first public game. */
function showPublicIntro(): void {
  shell(`
    <div class="screen public-intro">
      <h2 class="pi-title">Play online</h2>
      <p>Drop straight into a shared dish with anyone else online right now — no sign-up, no waiting. Empty seats fill with bots, so it is fun even if you are first in.</p>
      <p class="fine"><b>One thing to know:</b> online play is peer-to-peer — your browser connects <i>directly</i> to the others in the dish, so they can see your IP address. That is true of any P2P game and there is no server here to hide behind. Want to keep it to people you invite? Use <b>Play with friends</b> instead.</p>
      <div class="menu-actions">
        <button class="btn primary" id="pi-go">I'm in — play online</button>
        <button class="btn ghost" id="pi-back">Back</button>
      </div>
    </div>`);
  app.querySelector('#pi-go')!.addEventListener('click', () => {
    store.set('seen-public', true);
    startMatchmaking();
  });
  app.querySelector('#pi-back')!.addEventListener('click', showMenu);
}

function startMatchmaking(): void {
  teardownRoom();
  roomPublic = true;
  openBoard();
  shell(`
    <div class="screen matchmaking">
      <h1 class="title">Morsel</h1>
      <div class="mm-status"><span class="spinner" aria-hidden="true"></span> <span>Finding a dish…</span></div>
      <p class="pub-count mm-count"></p>
      <button class="btn ghost" id="mm-cancel">Back</button>
    </div>`);
  app.querySelector('#mm-cancel')!.addEventListener('click', showMenu);
  paintPublicCounts();
  // Give the board a moment to hear the open dishes, then join the busiest one
  // with a free seat — fill, don't spill — or host a fresh dish if there is none.
  matchTimer = setTimeout(pickAndJoin, 1600);
}

function pickAndJoin(): void {
  matchTimer = null;
  const joinable = publicRooms
    .filter((r) => r.players < r.max)
    .sort((a, b) => b.players - a.players);
  if (joinable.length) enterRoom(joinable[0].code, false, true);
  else enterRoom(mintCode(), true, true);
}

/** Advertise this dish while we hold the host role; stop the moment we do not. */
function startAnnouncing(): void {
  if (announceTimer) clearInterval(announceTimer);
  const tick = (): void => {
    if (!net || !board) return;
    if (net.isHost()) {
      board.announce({
        code: roomCode,
        host: myName,
        players: net.count() + 1,
        max: PUBLIC_MAX,
        playing: rounds?.state().phase === 'playing',
      });
    } else {
      board.unannounce();
    }
    paintPublicCounts();
  };
  announceTimer = setInterval(tick, 2000);
  tick();
}

/**
 * Join the room ONCE. Everything after this — the lobby, round 1, every
 * rematch — happens inside this Net. It is torn down only on the way back to
 * the menu. `isPublic` opens the dish to anyone (minPlayers 1 so it starts
 * instantly with bots, auto-ready, and advertised on the public list).
 */
function enterRoom(code: string, created: boolean, isPublic = false): void {
  teardownRoom();
  if (!isPublic) closeBoard();
  roomPublic = isPublic;
  roomCode = code;
  setRoomInUrl(code);

  net = createNet(
    // `claimHost` ONLY for the peer that minted the code. A typed code or a
    // link joins as a guest and waits to hear from the incumbent — otherwise
    // two peers race to host the same room.
    { appId: APP_ID, roomId: code, claimHost: created },
    {
      onHostChange: (_id, isSelfHost) => {
        session?.setHost(isSelfHost);
        if (session && isSelfHost) flashHud("The host left — you're the host now");
      },
      onPeerLeave: (id) => session?.onPeerLeave(id),
    },
  );

  rounds = createRounds({
    net,
    playerName: myName,
    // A public dish starts with one human (bots fill the rest); a private room
    // still waits for a friend.
    minPlayers: isPublic ? 1 : 2,
    // The host's mode travels FROZEN inside the round start. A setting each peer
    // reads from its own UI is a setting two peers can disagree about — and here
    // it would mean two players in differently-sized dishes on the same seed.
    roundOpts: () => ({ mode: mode.id }),
    onRound: (info) => {
      const opts = info.opts as { mode?: unknown } | undefined;
      // Always validate an id off the wire: an unknown one must fall back, never
      // reach the sim as undefined.
      liveMode = modeOf(opts?.mode);
      startRound(info.seed, liveMode, info.players, info.isHost);
    },
  });

  showLobby();

  if (isPublic) {
    // Auto-ready so the dish starts the instant the mesh settles (bots fill the
    // seats), and advertise it so others drop in.
    rounds.vote();
    startAnnouncing();
  }
}

function showLobby(): void {
  if (!net || !rounds) return showMenu();
  shell('<div class="screen" id="lobby"></div>');
  const box = app.querySelector<HTMLElement>('#lobby')!;
  const lobby = createLobby({
    container: box,
    net,
    rounds,
    roomCode,
    minPlayers: roomPublic ? 1 : 2,
    maxPlayers: MAX_PLAYERS,
    onCancel: showMenu,
  });

  // A public dish shows the live head-count as social proof; a private room does
  // not (it is just you and the friends you invited). Mount it OUTSIDE the lobby
  // box — createLobby owns that container and rewrites it on every re-render,
  // which would wipe an element appended inside.
  if (roomPublic) {
    const counts = el('<p class="pub-count lobby-count"></p>');
    box.parentElement?.appendChild(counts);
    paintPublicCounts();
  }

  // The lobby view owns the roster; this strip shows what the HOST has picked,
  // which is the only mode that matters. Guests see the gossiped value — never
  // their own local pick dressed up as the host's.
  const strip = el('<div class="lobby-mode"></div>');
  box.appendChild(strip);
  const paint = (): void => {
    if (!rounds || !net) return;
    const s = rounds.state();
    const hostOpts = s.hostOpts as { mode?: unknown } | null;
    const shown = modeOf(hostOpts?.mode);
    strip.innerHTML = net.isHost()
      ? `<span class="lm-label">Your dish (everyone plays this)</span>
         <div class="lm-modes">${MODE_LIST.map(
           (m) =>
             `<button class="lm${m.id === mode.id ? ' on' : ''}" data-mode="${m.id}">${m.name}</button>`,
         ).join('')}</div>
         <span class="lm-blurb">${esc(mode.blurb)}</span>`
      : hostOpts
        ? `<span class="lm-label">The host picked</span>
           <div class="lm-modes"><button class="lm on" disabled>${shown.name}</button></div>
           <span class="lm-blurb">${esc(shown.blurb)}</span>`
        : `<span class="lm-label"><span class="spinner sm"></span> Waiting for the host's dish…</span>`;
    for (const b of strip.querySelectorAll<HTMLElement>('.lm[data-mode]')) {
      b.addEventListener('click', () => {
        mode = modeOf(b.dataset.mode);
        store.set('mode', mode.id);
        sfx.play('select');
        paint();
      });
    }
  };
  paint();
  const poll = setInterval(paint, 700);

  cleanupLobby = () => {
    clearInterval(poll);
    lobby.destroy();
  };
}

let cleanupLobby: (() => void) | null = null;

// ── the round ───────────────────────────────────────────────────────────────

function startSolo(): void {
  teardownRoom();
  closeBoard();
  roundNo++;
  const seed = newSeed();
  startRound(seed, mode, [{ id: 'solo', name: myName }], true);
}

function startRound(
  seed: number,
  m: Mode,
  players: { id: string; name: string }[],
  isHost: boolean,
): void {
  cleanupLobby?.();
  cleanupLobby = null;
  countdown?.cancel();

  const humans = players.length;
  // Empty seats fill with bots, so a 2-player room is still a busy dish and a
  // solo player is never alone in a lake.
  const total = Math.max(m.seats, humans);
  const seats: Seat[] = [];
  const sseats: SessionSeat[] = [];
  const taken = new Set(players.map((p) => p.name));
  let botN = 0;
  for (let i = 0; i < total; i++) {
    if (i < humans) {
      seats.push({ name: players[i].name, bot: false });
      sseats.push({ id: players[i].id, bot: false });
    } else {
      let nm = BLOB_NAMES[botN % BLOB_NAMES.length];
      while (taken.has(nm)) nm = `${nm}${++botN}`;
      taken.add(nm);
      botN++;
      seats.push({ name: nm, bot: true });
      sseats.push({ bot: true });
    }
  }

  // Our seat comes from the HOST'S FROZEN ROSTER, never from a local lookup by
  // name: two players called "Blob" would otherwise land on the same seat and
  // watch each other's score.
  const me = net ? players.findIndex((p) => p.id === net!.selfId) : 0;
  mySeat = me >= 0 ? me : 0;
  game = new Game({ seed, mode: m, seats });
  const g = game;

  session = createSession({
    game: g,
    me,
    seats: sseats,
    net: net ?? undefined,
    host: isHost,
    seed,
    onEnd: () => showResults(),
  });

  showGame(g, me, m);
}

function showGame(g: Game, me: number, m: Mode): void {
  shell(`
    <div class="play">
      <canvas id="cv" class="drag-surface"></canvas>
      <div class="hud">
        <div class="hud-l">
          <div class="clock" id="clock">0:00</div>
          <div class="bloom" id="bloom"></div>
        </div>
        <ol class="board" id="board"></ol>
        <div class="hud-r">
          <button class="icon" id="pause" aria-label="Pause">II</button>
        </div>
      </div>
      <div class="dashwrap"><button class="dashbtn" id="dash" aria-label="Dash">DASH</button></div>
      <div class="flash" id="flash" role="status" aria-live="polite"></div>
      <div class="big" id="big" hidden></div>
      <div class="overlay" id="pausebox" hidden>
        <div class="modal-card">
          <h2>Paused</h2>
          <button class="btn primary" id="resume">Resume</button>
          <button class="btn" id="restart">Restart</button>
          <button class="btn ghost" id="quit">Menu</button>
        </div>
      </div>
    </div>`);
  document.body.classList.add('playing'); // hide the footer while the round is live

  const canvas = app.querySelector<HTMLCanvasElement>('#cv')!;
  const renderer = createRenderer(canvas);
  const fx = createFx();
  const input = createInput({
    target: canvas,
    keys: {
      Space: 'dash',
      KeyP: 'pause',
      Escape: 'pause',
      KeyM: 'mute',
    },
    // This game aims; it does not step. The D-pad overlay would be wrong, so we
    // use input.ts's pointer path and give dash its own big button below.
    touch: false,
  });

  const clockEl = app.querySelector<HTMLElement>('#clock')!;
  const bloomEl = app.querySelector<HTMLElement>('#bloom')!;
  const boardEl = app.querySelector<HTMLElement>('#board')!;
  const bigEl = app.querySelector<HTMLElement>('#big')!;
  const pauseBox = app.querySelector<HTMLElement>('#pausebox')!;
  let paused = false;
  let dashQueued = false;

  const dashBtn = app.querySelector<HTMLElement>('#dash')!;
  const fireDash = (e: Event): void => {
    e.preventDefault();
    dashQueued = true;
  };
  dashBtn.addEventListener('pointerdown', fireDash);

  // Steering. On a MOUSE the blob aims at the cursor (precise, no occlusion). On
  // TOUCH that meant reaching across and covering the blob to point where you
  // wanted to go — so touch now gets a FLOATING JOYSTICK: rest a thumb anywhere,
  // push the way you want to swim. A quick tap (no real push) dashes.
  let lastPointerType = 'mouse';
  let joyStart = 0;
  let joyMax = 0;
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const joystick = createJoystick({
    surface: canvas,
    reducedMotion,
    onStart: () => {
      joyStart = performance.now();
      joyMax = 0;
    },
    onChange: (v) => {
      if (v.mag > joyMax) joyMax = v.mag;
    },
    onEnd: () => {
      if (performance.now() - joyStart < 250 && joyMax < 0.25) dashQueued = true;
    },
  });
  canvas.addEventListener('pointerdown', (e) => {
    lastPointerType = e.pointerType;
    if (e.pointerType === 'mouse') dashQueued = true;
  });
  canvas.addEventListener('pointermove', (e) => {
    lastPointerType = e.pointerType;
  });

  app.querySelector('#pause')!.addEventListener('click', () => setPaused(true));
  app.querySelector('#resume')!.addEventListener('click', () => setPaused(false));
  app.querySelector('#restart')!.addEventListener('click', () => {
    if (net) {
      setPaused(false);
      return; // a shared round is not one player's to restart
    }
    loop.stop();
    input.destroy();
    startSolo();
  });
  app.querySelector('#quit')!.addEventListener('click', () => {
    loop.stop();
    input.destroy();
    showMenu();
  });

  function setPaused(p: boolean): void {
    // In a live room the world does not stop for you — pause is a menu, not a
    // freeze, or one player could hold the round hostage.
    paused = p && !net;
    pauseBox.hidden = !p;
    if (!p) hudTouch();
  }

  const resize = (): void => {
    const r = canvas.parentElement!.getBoundingClientRect();
    renderer.resize(r.width, r.height, Math.min(2, window.devicePixelRatio || 1));
  };
  const ro = new ResizeObserver(resize);
  ro.observe(canvas.parentElement!);
  resize();

  // 3-2-1-GO. The audio carries it: players watch the dish, not the overlay.
  let running = false;
  bigEl.hidden = false;
  countdown = startCountdown({
    onBeat: (n) => {
      bigEl.textContent = n > 0 ? String(n) : 'GO';
      bigEl.className = 'big pop';
      void bigEl.offsetWidth;
      bigEl.className = 'big pop go';
      sfx.play(n > 0 ? 'beat' : 'go');
    },
    onDone: () => {
      running = true;
      bigEl.hidden = true;
    },
  });

  function hudTouch(): void {
    const secs = Math.ceil(g.remaining());
    clockEl.textContent = `${Math.floor(secs / 60)}:${String(secs % 60).padStart(2, '0')}`;
    clockEl.classList.toggle('urgent', secs <= 15);
    const b = g.bloom();
    bloomEl.textContent = `Pellets ×${b.toFixed(1)}`;
    bloomEl.style.opacity = String(0.5 + ((b - 1) / (m.bloomMax - 1)) * 0.5);

    const top = g.standings().slice(0, 5);
    boardEl.innerHTML = top
      .map(
        (bl) =>
          `<li class="${bl.i === me ? 'me' : ''}"><i style="background:${colorOf(bl.i)}"></i>
           <span>${esc(bl.name)}</span><b>${Math.round(g.score(bl))}</b></li>`,
      )
      .join('');
  }

  // The HUD and the round's end run off setInterval, NOT rAF: a backgrounded
  // tab pauses rAF, and a clock that freezes when you glance away is worse than
  // no clock at all.
  const hudTimer = setInterval(() => {
    hudTouch();
    if (!paused) session?.pump(performance.now());
  }, 250);

  const loop = createLoop({
    update: () => {},
    render: () => {
      const now = performance.now();
      const dt = Math.min(0.05, (now - lastFrame) / 1000);
      lastFrame = now;

      if (!paused && running) {
        const self = g.blobs[me];
        if (joystick.active()) {
          // Touch: swim the way the thumb is pushing. Inside the dead zone the
          // vector is (0,0), which reads as drift — exactly what we want.
          const v = joystick.vector();
          session?.intent(v.x, v.y, false);
        } else if (lastPointerType === 'mouse' && self) {
          // Desktop: aim at the cursor, as before.
          const p = input.state.pointer;
          if (p) {
            const w = renderer.toWorld(p.x, p.y);
            const dx = w.x - self.x;
            const dy = w.y - self.y;
            // A dead zone inside the blob, or it jitters when you point at yourself.
            const d = Math.hypot(dx, dy);
            if (d > radiusOf(self.mass) * 0.35) session?.intent(dx, dy, false);
            else session?.intent(0, 0, false);
          }
        } else {
          // Touch with no thumb down → drift to a stop.
          session?.intent(0, 0, false);
        }
        const wantDash = dashQueued || input.state.pressed.has('dash');
        if (wantDash) session?.intent(self ? self.ax : 0, self ? self.ay : 0, true);
        dashQueued = false;

        if (fx.stopped() <= 0) session?.pump(now);
      }

      if (input.state.pressed.has('pause')) setPaused(!pauseBox.hidden ? false : true);
      if (input.state.pressed.has('mute')) {
        sfx.setMuted(!sfx.muted());
        store.set('muted', sfx.muted());
      }

      fx.step(dt);
      drainEvents(g, me, fx);
      renderer.draw(g, me, fx, 0, dt);
      input.endFrame();
    },
  });

  let lastFrame = performance.now();
  loop.start();

  cleanupGame = () => {
    loop.stop();
    input.destroy();
    joystick.destroy();
    ro.disconnect();
    clearInterval(hudTimer);
    countdown?.cancel();
    countdown = null;
  };
  hudTouch();
}

let cleanupGame: (() => void) | null = null;

/** Turn sim events into noise and light. The sim stays pure; this is the theatre. */
function drainEvents(g: Game, me: number, fx: ReturnType<typeof createFx>): void {
  for (const e of g.events) {
    switch (e.k) {
      case 'pellet': {
        fx.burst(e.x, e.y, e.food ? 3 : 4, e.food ? '#f0e442' : colorOf(g.blobs[e.i].i), 60, 2);
        // Only the local player's grazing makes a sound, or a busy dish is a
        // wall of blips. Pitch falls as you grow — growth you can hear.
        if (e.i === me) sfx.play('pellet', g.blobs[me].mass);
        break;
      }
      case 'dash':
        fx.burst(e.x, e.y, 8, colorOf(e.i), 150, 3);
        if (e.i === me) sfx.play('dash');
        break;
      case 'eat': {
        const big = Math.min(1, e.mass / 200);
        fx.burst(e.x, e.y, 14 + Math.round(big * 20), colorOf(e.j), 200 + big * 200, 4);
        fx.ring(e.x, e.y, colorOf(e.j), radiusOf(e.mass));
        if (e.j === me) {
          // You were eaten. It should sting.
          fx.shake(16);
          fx.stop(0.09);
          sfx.play('eaten');
          flashHud('Swallowed! Back in a moment…');
        } else if (e.i === me) {
          fx.shake(3 + big * 7);
          fx.stop(0.09); // the frame that makes a swallow land
          sfx.play('swallow');
        }
        break;
      }
      case 'spawn':
        fx.ring(e.x, e.y, colorOf(e.i), radiusOf(START_MASS) * 2);
        if (e.i === me) sfx.play('spawn');
        break;
      case 'bloom':
        sfx.play('bloom');
        break;
    }
  }
  g.events.length = 0;
}

function flashHud(msg: string): void {
  const f = document.querySelector<HTMLElement>('#flash');
  if (!f) return;
  f.textContent = msg;
  f.classList.add('show');
  setTimeout(() => f.classList.remove('show'), 2200);
}

// ── results ─────────────────────────────────────────────────────────────────

function showResults(): void {
  cleanupGame?.();
  cleanupGame = null;
  const g = game;
  if (!g) return showMenu();

  sfx.play('whistle');
  const s = summarize(g, mySeat, tally);
  tally = tallyRound(tally, s);
  roundNo++;

  const mine = s.rows.find((r) => r.isSelf);
  if (mine && !net) {
    const best = store.get<number>(`best:${g.mode.id}`, 0);
    if (mine.mass > best) store.set(`best:${g.mode.id}`, mine.mass);
  }
  if (s.winner?.isSelf) sfx.play('win');

  shell(`
    <div class="results">
      <h2 class="rs-title">${esc(g.mode.name)} — round over</h2>
      <div id="rsbody">${renderSummary(s, g, tally, roundNo)}</div>
      <div class="rs-wait" id="rswait" hidden></div>
      <div class="rs-actions">
        <button class="btn primary" id="again">Play again</button>
        <button class="btn" id="share">Share</button>
        ${net ? '<button class="btn ghost" id="tolobby">Back to lobby</button>' : ''}
        <button class="btn ghost" id="menu">Menu</button>
      </div>
    </div>`);

  app.querySelector('#share')!.addEventListener('click', () => void share(shareText(s, g)));
  app.querySelector('#menu')!.addEventListener('click', showMenu);
  app.querySelector('#tolobby')?.addEventListener('click', () => {
    // Back to the lobby does NOT leave the room — the Net and the whole mesh
    // stay up. Leaving would re-arm the rejoin trap.
    rounds?.finish();
    showLobby();
  });

  const again = app.querySelector<HTMLElement>('#again')!;
  const wait = app.querySelector<HTMLElement>('#rswait')!;

  if (!net) {
    again.addEventListener('click', () => startSolo());
    return;
  }

  rounds?.finish();
  // A public dish never stops: auto-ready for the next round so play loops and
  // anyone who joined mid-round gets into the next one. The results still show
  // during the start countdown.
  if (roomPublic) {
    rounds?.vote();
    again.setAttribute('disabled', '');
    again.textContent = 'Next dish…';
  }
  again.addEventListener('click', () => {
    rounds?.vote();
    again.setAttribute('disabled', '');
    again.textContent = 'Waiting…';
    paintWait();
  });

  // A silent wait is indistinguishable from a hang. Show who is in, and the
  // countdown that will start the round without the stragglers.
  function paintWait(): void {
    if (!rounds || !net) return;
    const st = rounds.state();
    if (st.phase === 'playing') return;
    const votes = st.votes.map((v) => esc(v.name)).join(', ');
    const missing = st.present.length - st.votes.length;
    wait.hidden = st.votes.length === 0;
    wait.innerHTML = `
      <span class="spinner sm" aria-hidden="true"></span>
      <span>${votes || 'Nobody'} ready${missing > 0 ? ` · waiting on ${missing}` : ''}${
        st.startsInMs != null
          ? ` · starting in ${Math.ceil(st.startsInMs / 1000)}s`
          : st.votes.length >= 2
            ? ''
            : ' · need 2 to play'
      }</span>
      ${st.isHost && st.canStart ? '<button class="btn sm" id="force">Start now</button>' : ''}`;
    wait.querySelector('#force')?.addEventListener('click', () => rounds?.go());
  }
  const poll = setInterval(paintWait, 400);
  cleanupGame = () => clearInterval(poll);
  paintWait();
}

async function share(text: string): Promise<void> {
  try {
    if (navigator.share) {
      await navigator.share({ title: 'Morsel', text });
      return;
    }
  } catch {
    /* cancelled — fall through to copy */
  }
  try {
    await navigator.clipboard.writeText(text);
    flashHud('Copied!');
  } catch {
    flashHud('Copy failed — select and copy manually');
  }
}

// ── teardown ────────────────────────────────────────────────────────────────

function teardownRoom(): void {
  cleanupGame?.();
  cleanupGame = null;
  cleanupLobby?.();
  cleanupLobby = null;
  countdown?.cancel();
  countdown = null;
  if (announceTimer) {
    clearInterval(announceTimer);
    announceTimer = null;
  }
  roomPublic = false;
  session?.destroy();
  session = null;
  rounds?.destroy();
  rounds = null;
  if (net) {
    // Genuinely leaving. `leave()` resolves only once Trystero has really let
    // go, so a later join of the same code cannot alias the dying room.
    void net.leave();
    net = null;
  }
  game = null;
  roundNo = 0;
  tally = emptyTally();
  // Note: the noticeboard (public list) has its OWN lifecycle — a public join
  // tears down and re-enters the game room without dropping the board. It is
  // closed explicitly by closeBoard() on the way to the menu / a private room.
}

window.addEventListener('beforeunload', () => {
  void net?.leave();
});

// ── boot ────────────────────────────────────────────────────────────────────

/**
 * TURN credentials are fetched ONCE, here, before any screen exists — and that
 * ordering is load-bearing, not tidiness.
 *
 * Trystero builds a single global connection pool from the config of the FIRST
 * joinRoom() on the page. Whichever mesh opens first wins, permanently: a
 * setTurnConfig() that lands afterwards is silently ignored and leaves the
 * initiating half of every peer pair STUN-only, which is exactly the pair that
 * fails on carrier-grade NAT. This page opens two kinds of mesh — the game room
 * and the public noticeboard — and the menu can reach the board before anyone
 * enters a room, so the only safe place for this call is ahead of both.
 *
 * getTurnConfig() is session-cached, times out in 3s and fails open to [] (plain
 * STUN, the old behaviour), so it can slow a boot but can never block one.
 */
async function boot(): Promise<void> {
  setTurnConfig(await getTurnConfig());

  // A ?room= link is honoured ONCE, then cleared. Leave it in the URL and a
  // reload — or reopening from a home-screen icon — silently drags the player
  // back into a room they left, with no way to start a new one.
  const url = new URL(location.href);
  const deep = url.searchParams.get('room');
  if (deep && !deepLinkUsed) {
    deepLinkUsed = true;
    const code = normalizeRoomCode(deep);
    if (code.length >= 3) enterRoom(code, false);
    else showMenu();
  } else {
    showMenu();
  }
}

void boot();
